import { type AudioCapabilities, chooseRoutingStrategy } from "./capabilities"

type SinkSelectableContext = AudioContext & {
  setSinkId(sinkId: string): Promise<void>
}

type SinkSelectableElement = HTMLAudioElement & {
  setSinkId(sinkId: string): Promise<void>
}

type OutputPicker = MediaDevices & {
  selectAudioOutput(options?: { readonly deviceId?: string }): Promise<MediaDeviceInfo>
}

function canSetContextSink(context: AudioContext): context is SinkSelectableContext {
  return "setSinkId" in context
}

function canSetElementSink(element: HTMLAudioElement): element is SinkSelectableElement {
  return "setSinkId" in element
}

function canPickOutput(devices: MediaDevices): devices is OutputPicker {
  return "selectAudioOutput" in devices
}

export class OutputRouteError extends Error {
  readonly causeName: string

  constructor(message: string, causeName: string) {
    super(message)
    this.name = "OutputRouteError"
    this.causeName = causeName
  }
}

export class AudioRouter {
  readonly input: GainNode
  readonly monitorInput: GainNode
  readonly capabilities: AudioCapabilities
  private readonly limiter: DynamicsCompressorNode
  private readonly routedElement: HTMLAudioElement
  private mediaDestination: MediaStreamAudioDestinationNode | undefined

  constructor(
    private readonly context: AudioContext,
    routedElement: HTMLAudioElement,
  ) {
    this.routedElement = routedElement
    this.input = context.createGain()
    this.monitorInput = context.createGain()
    this.monitorInput.gain.value = 0
    this.limiter = context.createDynamicsCompressor()
    this.limiter.threshold.value = -6
    this.limiter.knee.value = 3
    this.limiter.ratio.value = 20
    this.limiter.attack.value = 0.003
    this.limiter.release.value = 0.15
    this.input.connect(this.limiter)
    this.monitorInput.connect(this.limiter)
    this.limiter.connect(context.destination)
    this.capabilities = {
      contextSink: canSetContextSink(context),
      mediaElementSink: canSetElementSink(routedElement),
      outputPicker: canPickOutput(navigator.mediaDevices),
    }
  }

  setMonitoring(enabled: boolean, levelDecibels: number): void {
    const target = enabled ? 10 ** (levelDecibels / 20) : 0
    this.monitorInput.gain.setTargetAtTime(target, this.context.currentTime, 0.02)
  }

  async pickOutput(): Promise<MediaDeviceInfo | undefined> {
    if (!canPickOutput(navigator.mediaDevices)) return undefined
    return navigator.mediaDevices.selectAudioOutput()
  }

  async setOutput(deviceId: string): Promise<void> {
    const strategy = chooseRoutingStrategy(this.capabilities)
    const sinkId = deviceId === "default" ? "" : deviceId
    try {
      switch (strategy.kind) {
        case "audio_context":
          this.connectToContextDestination()
          if (canSetContextSink(this.context)) await this.context.setSinkId(sinkId)
          return
        case "media_element":
          await this.connectToMediaElement(sinkId)
          return
        case "system_default":
          this.connectToContextDestination()
          return
      }
    } catch (cause) {
      if (cause instanceof DOMException) {
        throw new OutputRouteError(cause.message, cause.name)
      }
      throw cause
    }
  }

  private connectToContextDestination(): void {
    this.limiter.disconnect()
    this.routedElement.pause()
    this.routedElement.srcObject = null
    this.limiter.connect(this.context.destination)
  }

  private async connectToMediaElement(sinkId: string): Promise<void> {
    this.mediaDestination ??= this.context.createMediaStreamDestination()
    this.limiter.disconnect()
    this.limiter.connect(this.mediaDestination)
    this.routedElement.srcObject = this.mediaDestination.stream
    if (canSetElementSink(this.routedElement)) await this.routedElement.setSinkId(sinkId)
    await this.routedElement.play()
  }
}
